import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

/**
 * Photos the technician took on a job (Phase 2, 20260912100700_job_photos.sql).
 * Rows live in public.job_photos; objects in the private bucket job-photos at
 * <company_id>/<job_id>/<uuid>.jpg, so every URL is a short-lived signed URL.
 * Contract from the migration: delete the object first, then the row.
 */

export const JOB_PHOTOS_BUCKET = 'job-photos';
const SIGNED_URL_TTL_SECONDS = 60 * 60;

export interface JobPhoto {
  id: string;
  job_id: string;
  company_id: string;
  storage_path: string;
  caption: string | null;
  taken_by: string;
  taken_by_tech_id: string | null;
  width: number | null;
  height: number | null;
  bytes: number | null;
  created_at: string;
  tech: { name: string } | null;
  /** Signed URL, valid for an hour; null when signing failed for this object. */
  url: string | null;
}

export function useJobPhotos(jobId: string | null) {
  return useQuery({
    queryKey: ['job-photos', jobId],
    queryFn: async (): Promise<JobPhoto[]> => {
      if (!jobId) return [];
      const { data, error } = await supabase
        .from('job_photos')
        .select('id, job_id, company_id, storage_path, caption, taken_by, taken_by_tech_id, width, height, bytes, created_at, tech:techs!job_photos_taken_by_tech_id_fkey(name)')
        .eq('job_id', jobId)
        .order('created_at', { ascending: true });
      if (error) throw error;
      const rows = (data ?? []) as unknown as Omit<JobPhoto, 'url'>[];
      if (rows.length === 0) return [];

      const { data: signed, error: signError } = await supabase.storage
        .from(JOB_PHOTOS_BUCKET)
        .createSignedUrls(rows.map((r) => r.storage_path), SIGNED_URL_TTL_SECONDS);
      if (signError) throw signError;
      const urlByPath = new Map((signed ?? []).map((s) => [s.path, s.error ? null : s.signedUrl]));

      return rows.map((r) => ({ ...r, url: urlByPath.get(r.storage_path) ?? null }));
    },
    enabled: !!jobId,
    // Signed URLs expire; refetch well inside the TTL when the card is still open.
    staleTime: (SIGNED_URL_TTL_SECONDS / 2) * 1000,
  });
}

export function useDeleteJobPhoto() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ photo }: { photo: Pick<JobPhoto, 'id' | 'job_id' | 'storage_path'> }) => {
      // Object first (storage RLS: staff or the uploader), then the row.
      const { error: storageError } = await supabase.storage.from(JOB_PHOTOS_BUCKET).remove([photo.storage_path]);
      if (storageError) throw storageError;
      const { error } = await supabase.from('job_photos').delete().eq('id', photo.id);
      if (error) throw error;
    },
    onSuccess: (_, { photo }) => {
      queryClient.invalidateQueries({ queryKey: ['job-photos', photo.job_id] });
      toast({ title: 'Photo deleted' });
    },
    onError: (error: Error) => {
      toast({ title: 'Failed to delete photo', description: error.message, variant: 'destructive' });
    },
  });
}
